import { Link, Button, Card, Collapse, DatePicker, Divider, Form, Grid, Modal, Space, Tag, Timeline, Typography } from "@arco-design/web-react"
import { Fragment, useCallback, useEffect, useState } from "react";
import { BankAPI, ReconciliationAPI } from "@/apis/banking";
import { InputNumber } from "@/components/Numeric";
import { Currency } from "@/components/Numeric";
import _ from "lodash";
import { IconCheckCircleFill, IconExclamationCircleFill, IconPlusCircleFill } from "@arco-design/web-react/icon";
import "./index.css";
import { useNavigate } from "react-router-dom";
import { ReconciliationDetail } from "./reconciliation-detail";

const TimelineItem = Timeline.Item;
const CollapseItem = Collapse.Item;
const { Title, Paragraph, Text } = Typography;
const { Row, Col } = Grid;


const ReconcileButton = ({ bankAccountId, onClick, ...props }) => {
    const [visible, setVisible] = useState(false);
    const [account, setAccount] = useState({});
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const getAccount = useCallback(async () => {
        const account = await BankAPI.account(bankAccountId);
        setAccount(account);
    }, [bankAccountId]);

    const onOk = async () => {
        const values = await form.validate();
        const reconciliationId = await ReconciliationAPI.reconcile(bankAccountId, values);
        navigate(`/workspace/accounting/reconciliation/${reconciliationId}`);
    }

    useEffect(() => {
        getAccount();
    }, [getAccount])

    return (
        <>
            <Button {...props} type="primary" onClick={() => {
                setVisible(true);
            }}>
                Reconcile
            </Button>
            <Modal title="Reconciliation" visible={visible}
                afterClose={() => {
                    form.resetFields();
                }}
                onOk={onOk}
                onCancel={() => {
                    setVisible(false);
                }}
            >
                <Typography>
                    <Title heading={5}>{_.capitalize(account.subType)} **** {account.mask}</Title>
                    <Text>Enter the ending balance as it appears on your statement</Text>
                </Typography>
                <Form
                    style={{ marginTop: 28 }}
                    layout="vertical"
                    form={form}
                >
                    <Form.Item label="Balance Date" field="endDate">
                        <DatePicker />
                    </Form.Item>
                    <Form.Item label="Balance Amount" field="statementBalance">
                        <InputNumber />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    )
}

export const Reconciliation = () => {
    const [institutions, setInstitutions] = useState([]);

    const getAccountReconciliation = async (bankAccountId) => {
        const reconciliations = await ReconciliationAPI.reconciliations(bankAccountId);
        const pendingTransactionCount = await ReconciliationAPI.pendingTransactions(bankAccountId);
        return {
            records: reconciliations,
            pendingTransactionCount
        }
    };

    const getInstitutions = useCallback(async () => {
        // 对账页专用的机构列表（含已删除连接）：
        // 删除连接后账户与历史对账记录仍需可见、可查看
        let institutions = await BankAPI.reconciliationInstitutions();

        // 列出所有已绑定系统账户的银行账户（不要求开启自动同步，含软删除）：
        // 删除连接后账户与历史对账记录仍需展示，重连同一账户后历史记录也不会丢失
        const accounts = await BankAPI.reconciliationAccounts();
        const accountsByInstitution = _.groupBy(accounts, 'institutionId');
        for (const institution of institutions) {
            institution.accounts = accountsByInstitution[institution.institutionId] ?? [];
            for (const account of institution.accounts) {
                account.reconciliation = await getAccountReconciliation(account.id);
            }
        }

        institutions = _.filter(institutions, (o) => {
            return _.size(o.accounts) > 0;
        });

        setInstitutions(institutions);
    }, []);

    useEffect(() => {
        getInstitutions();
    }, [getInstitutions])

    return (
        <Card title="Reconciliation">
            {_.size(institutions) > 0 &&
                <Collapse accordion
                >
                    {institutions.map((item) => {
                        return (
                            <CollapseItem key={item.institutionId}
                                className="collapse-item"
                                header={
                                    <>
                                        <Space>
                                            <Text bold>{item.institutionName}</Text>
                                            {item.deleted === true &&
                                                <Tag color="gray" bordered={false}>Deleted</Tag>
                                            }
                                        </Space>
                                    </>
                                }>
                                {item.accounts && item.accounts.map((account, index) => (
                                    <Fragment key={index}>
                                        <Row>
                                            <Col span={12}>
                                                <Typography>
                                                    {index > 0 &&
                                                        <Divider />
                                                    }
                                                    <Title heading={6}>
                                                        {account.institutionAccountName}
                                                    </Title>

                                                    <Paragraph>
                                                        <Space direction="vertical">
                                                            <Text>
                                                                {_.capitalize(account.subType)} **** {account.mask}
                                                                {account.deleted === true && " (Deleted)"}
                                                            </Text>
                                                            <Text>Current Balance: <Currency value={account.balance} /></Text>
                                                        </Space>
                                                    </Paragraph>
                                                </Typography>
                                            </Col>
                                            <Col span={12} style={{ textAlign: "right", top: 30 }}>
                                                <ReconcileButton bankAccountId={account.id} />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col span={24}>
                                                <Timeline direction="horizontal" mode="top" >
                                                    {account.reconciliation.records && account.reconciliation.records.map((record, index) => (
                                                        <TimelineItem key={index}
                                                            dot={
                                                                record.status === 1 ?
                                                                    <IconCheckCircleFill style={{ color: "var(--color-success-light-4)", fontSize: 24 }} />
                                                                    :
                                                                    <IconExclamationCircleFill style={{ color: "var(--color-warning-light-4)", fontSize: 24 }} />
                                                            }>
                                                            <Typography>
                                                                <Link href={`/workspace/accounting/reconciliation/${record.id}`} >
                                                                    {record.endDate}
                                                                </Link>
                                                            </Typography>
                                                        </TimelineItem>
                                                    ))}
                                                    <TimelineItem dot={
                                                        <IconPlusCircleFill style={{ color: "var(--color-fill-4)", fontSize: 24 }} />
                                                    }>
                                                        <Typography>
                                                            <Space>
                                                                <Text style={{ color: "var(--color-text-3)" }}>
                                                                    {account.reconciliation.pendingTransactionCount ?? 0} new transactions
                                                                </Text>
                                                            </Space>
                                                        </Typography>
                                                    </TimelineItem>
                                                </Timeline>
                                            </Col>
                                        </Row>
                                    </Fragment>
                                ))}
                            </CollapseItem>
                        )
                    })}
                </Collapse >
            }
        </Card >
    )
}

Reconciliation.Detail = ReconciliationDetail;